import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Loader2, ClipboardList, ArrowLeft, Check, BarChart3, Activity, Save, ChevronDown, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import PerformanceCard from './PerformanceCard';
import './PhotographerReport.css';

const API = process.env.REACT_APP_BACKEND_URL;

const BAND_INFO = {
  g: { label: 'On target', cls: 'g' },
  a: { label: 'Watch', cls: 'a' },
  r: { label: 'Action needed', cls: 'r' },
  n: { label: 'No forecast', cls: 'n' },
};

const Q_META = {
  celebrate: { label: 'Celebrate', cls: 'celebrate' },
  discuss: { label: 'Discuss', cls: 'discuss' },
  explore: { label: 'Explore', cls: 'explore' },
};

function PctBar({ label, cell, i }) {
  const [v, b] = cell || [null, 'n'];
  const num = v === null || v === '' ? null : parseInt(String(v).replace('%', ''));
  const width = num === null ? 0 : Math.max(4, Math.min(100, (num / 150) * 100));
  return (
    <div className="pr-qbar" style={{ animationDelay: `${180 + i * 90}ms` }}>
      <span className="pr-qbar-label">{label}</span>
      <div className="pr-qbar-track">
        <div className={`pr-qbar-fill pr-fill-${b}`} style={{ width: `${width}%`, transitionDelay: `${250 + i * 90}ms` }} />
      </div>
      <span className={`pr-qbar-val pr-text-${b}`}>{num === null ? '—' : `${num}%`}</span>
    </div>
  );
}

function QuestionCard({ q, i, covered, onToggle, note, onNote }) {
  const meta = Q_META[q.type] || Q_META.explore;
  return (
    <div
      className={`pr-question pr-q-${meta.cls} ${covered ? 'is-covered' : ''}`}
      style={{ animationDelay: `${i * 70}ms` }}
      data-testid={`pr-question-${i}`}
    >
      <button className="pr-q-check" onClick={onToggle} title={covered ? 'Mark as not covered' : 'Mark as covered'} data-testid={`pr-question-check-${i}`}>
        {covered && <Check size={13} />}
      </button>
      <div className="pr-q-main">
        <span className={`pr-q-badge pr-badge-${meta.cls}`}>{meta.label}</span>
        <p className="pr-q-text">{q.question}</p>
        <div className={`pr-note-wrap ${note ? 'has-note' : ''}`}>
          <PenLine size={13} className="pr-note-pen" />
          <textarea
            className="pr-q-note"
            placeholder="Jot a note from the conversation…"
            value={note}
            onChange={(e) => onNote(e.target.value)}
            rows={note ? Math.min(4, note.split('\n').length + 1) : 1}
            data-testid={`pr-note-${i}`}
          />
        </div>
      </div>
      {q.stat && (
        <div className={`pr-q-stat pr-stat-${q.stat.band}`}>
          <span className="pr-q-stat-label">{q.stat.label}</span>
          <span className="pr-q-stat-value">{q.stat.value}</span>
        </div>
      )}
    </div>
  );
}

export default function PhotographerReport({ memberId, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('traffic');
  const [showQuestions, setShowQuestions] = useState(false);
  const [covered, setCovered] = useState({});
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState([]);
  const [lastSaved, setLastSaved] = useState(null);
  const [openHistory, setOpenHistory] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/staff/report/${memberId}`);
        setReport(r.data);
        requestAnimationFrame(() => setMounted(true));
        try {
          const mr = await axios.get(`${API}/api/staff/meetings/${memberId}`);
          const recs = mr.data.records || [];
          setRecords(recs);
          const current = recs.find((x) => x.period === r.data.period);
          if (current) {
            const nm = {}; const cv = {};
            r.data.questions.forEach((q, i) => {
              const e = current.entries.find((en) => en.question === q.question);
              if (e) { if (e.note) nm[i] = e.note; if (e.covered) cv[i] = true; }
            });
            setNotes(nm);
            setCovered(cv);
            setLastSaved({ at: current.saved_at, by: current.saved_by_name });
          }
        } catch { /* meeting history is optional */ }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'No report available for this member');
        onClose();
      }
      setLoading(false);
    })();
  }, [memberId, onClose]);

  const saveMeeting = async () => {
    setSaving(true);
    try {
      const entries = report.questions.map((q, i) => ({
        question: q.question, type: q.type, stat: q.stat, covered: !!covered[i], note: notes[i] || '',
      }));
      const r = await axios.post(`${API}/api/staff/meeting/${memberId}`, { period: report.period, entries });
      setLastSaved({ at: r.data.saved_at, by: r.data.saved_by_name });
      toast.success('Meeting record saved — it will be here next quarter');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save meeting record');
    }
    setSaving(false);
  };

  if (loading) {
    return createPortal(<div className="pr-overlay"><div className="pr-loading"><Loader2 size={24} className="pr-spin" /></div></div>, document.body);
  }
  if (!report) return null;

  const row = report.traffic;
  const band = BAND_INFO[row.band] || BAND_INFO.n;
  const { grade } = row;
  const gradeParts = String(grade).split('>').map((p) => p.trim());
  const gradeCurrent = parseInt(gradeParts[gradeParts.length - 1]) || 0;
  const gradePrev = gradeParts.length > 1 ? parseInt(gradeParts[0]) : null;
  const hyPct = row.ytd?.hy?.[0];
  const coveredCount = Object.values(covered).filter(Boolean).length;

  return createPortal(
    <div className="pr-overlay" onClick={onClose} data-testid="photographer-report">
      <div className={`pr-sheet ${mounted ? 'is-in' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="pr-topbar">
          {showQuestions ? (
            <button className="pr-btn" onClick={() => setShowQuestions(false)} data-testid="pr-back-btn">
              <ArrowLeft size={13} /> Back to report
            </button>
          ) : <span />}
          <button className="pr-btn pr-close" onClick={onClose} data-testid="pr-close"><X size={15} /></button>
        </div>

        <div className="pr-body">
          {/* Header */}
          <div className="pr-header">
            <div className={`pr-photo pr-ring-${row.band}`}>
              {report.photo
                ? <img src={`${API}${report.photo}`} alt={report.name} />
                : <span className="pr-initials">{report.name.split(' ').map((p) => p[0]).slice(0, 2).join('')}</span>}
            </div>
            <div className="pr-head-info">
              <h2 className="pr-name">{report.name}</h2>
              <div className="pr-sub">{report.team} · {report.period}</div>
              <div className="pr-head-badges">
                <span className={`pr-band-pill pr-pill-${row.band}`} data-testid="pr-band-pill">{band.label}</span>
                <span className="pr-badge-grade">
                  Grade
                  <span className="pr-grade-dots">
                    {Array.from({ length: 9 }).map((_, i) => (
                      <i key={i} className={i < gradeCurrent ? 'on' : ''} style={{ animationDelay: `${200 + i * 55}ms` }} />
                    ))}
                  </span>
                  <b>{gradeCurrent}</b>
                </span>
                {gradePrev !== null && (
                  <span className="pr-takeover-pill" title={`The forecast is still set at the previous holder's Grade ${gradePrev} level`} data-testid="pr-takeover-pill">
                    Took over a Grade {gradePrev} round
                  </span>
                )}
              </div>
            </div>
            {!showQuestions && (
              <div className="pr-tabs" data-testid="pr-tabs">
                <button className={tab === 'traffic' ? 'is-active' : ''} onClick={() => setTab('traffic')} data-testid="pr-tab-traffic">
                  <BarChart3 size={13} /> Traffic Light
                </button>
                <button
                  className={`${tab === 'performance' ? 'is-active' : ''} ${report.has_performance ? '' : 'is-disabled'}`}
                  onClick={() => report.has_performance && setTab('performance')}
                  title={report.has_performance ? undefined : 'Performance card coming soon'}
                  data-testid="pr-tab-performance"
                >
                  <Activity size={13} /> Performance{!report.has_performance && <em>soon</em>}
                </button>
              </div>
            )}
          </div>

          {showQuestions ? (
            <div className="pr-questions" data-testid="pr-questions">
              <div className="pr-questions-head">
                <h3>Quarterly Questions</h3>
                <span className="pr-questions-progress">{coveredCount}/{report.questions.length} covered</span>
              </div>
              <p className="pr-questions-hint">Built from {report.name.split(' ')[0]}'s live numbers — the relevant stat sits beside each question so you can discuss it together. Tick them off and jot notes as you go, then save the meeting record.</p>
              {report.questions.map((q, i) => (
                <QuestionCard
                  key={i} q={q} i={i}
                  covered={!!covered[i]}
                  onToggle={() => setCovered((c) => ({ ...c, [i]: !c[i] }))}
                  note={notes[i] || ''}
                  onNote={(v) => setNotes((n) => ({ ...n, [i]: v }))}
                />
              ))}

              <div className="pr-save-row">
                <button className="pr-save-btn" onClick={saveMeeting} disabled={saving} data-testid="pr-save-meeting">
                  {saving ? <Loader2 size={15} className="pr-spin" /> : <Save size={15} />} Save meeting record
                </button>
                {lastSaved && (
                  <span className="pr-last-saved" data-testid="pr-last-saved">
                    Last saved {new Date(lastSaved.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} by {lastSaved.by}
                  </span>
                )}
              </div>

              {records.filter((rec) => rec.period !== report.period).length > 0 && (
                <div className="pr-history" data-testid="pr-history">
                  <h4>Previous meetings</h4>
                  {records.filter((rec) => rec.period !== report.period).map((rec) => (
                    <div key={rec.period} className="pr-history-item">
                      <button className="pr-history-head" onClick={() => setOpenHistory(openHistory === rec.period ? null : rec.period)} data-testid={`pr-history-${rec.period}`}>
                        <span>{rec.period}</span>
                        <span className="pr-history-meta">
                          {rec.saved_by_name} · {new Date(rec.saved_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          <ChevronDown size={14} className={openHistory === rec.period ? 'is-open' : ''} />
                        </span>
                      </button>
                      {openHistory === rec.period && (
                        <div className="pr-history-body">
                          {rec.entries.map((en, j) => (
                            <div key={j} className={`pr-history-entry ${en.covered ? 'was-covered' : ''}`}>
                              <p className="pr-history-q">{en.covered && <Check size={12} />} {en.question}</p>
                              {en.note && <p className="pr-history-note">{en.note}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : tab === 'performance' && report.has_performance ? (
            <div className="pr-perf-embed">
              <PerformanceCard memberId={memberId} embedded />
            </div>
          ) : (
            <div className="pr-traffic" data-testid="pr-traffic">
              {/* Income tiles */}
              <div className="pr-tiles">
                <div className="pr-tile" style={{ animationDelay: '100ms' }}>
                  <span className="pr-tile-label">2025 actual</span>
                  <span className="pr-tile-value">{row.a2025}</span>
                </div>
                <div className="pr-tile" style={{ animationDelay: '160ms' }}>
                  <span className="pr-tile-label">2026 forecast</span>
                  <span className="pr-tile-value pr-dim">{row.f2026}</span>
                </div>
                <div className="pr-tile" style={{ animationDelay: '220ms' }}>
                  <span className="pr-tile-label">2026 actual</span>
                  <span className="pr-tile-value pr-strong">{row.a2026}</span>
                </div>
                <div className={`pr-tile pr-tile-pct pr-tile-${row.band}`} style={{ animationDelay: '280ms' }}>
                  <span className="pr-tile-label">vs forecast</span>
                  <span className="pr-tile-value" data-testid="pr-hy-pct">{hyPct || '—'}</span>
                </div>
              </div>

              {/* Quarter by quarter */}
              <div className="pr-panel">
                <h4>Quarter by quarter — % against forecast</h4>
                <PctBar label="Q3 2025" cell={row.quarter.q3} i={0} />
                <PctBar label="Q4 2025" cell={row.quarter.q4} i={1} />
                <PctBar label="Q1 2026" cell={row.quarter.q1} i={2} />
                <PctBar label="Q2 2026" cell={row.quarter.q2} i={3} />
              </div>

              <div className="pr-panel">
                <h4>Year to date</h4>
                <PctBar label="Half year" cell={row.ytd.hy} i={0} />
                <PctBar label="Q1" cell={row.ytd.q1} i={1} />
              </div>

              <p className="pr-legend">
                <span className="pr-dot pr-fill-g" /> On / above forecast&nbsp;&nbsp;
                <span className="pr-dot pr-fill-a" /> Just below (watch)&nbsp;&nbsp;
                <span className="pr-dot pr-fill-r" /> Well below (action)&nbsp;&nbsp;
                <span className="pr-dot pr-fill-n" /> No forecast set
              </p>
            </div>
          )}
        </div>

        {!showQuestions && (
          <button className="pr-questions-btn" onClick={() => setShowQuestions(true)} data-testid="pr-questions-btn">
            <ClipboardList size={16} /> Quarterly Questions
            <span className="pr-questions-count">{report.questions.length}</span>
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
